import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { 
  Building, 
  MapPin, 
  Users, 
  Plus, 
  X, 
  AlertCircle,
  Shield
} from 'lucide-react';

interface UserScopesProps {
  onScopesChange?: (scopes: { sites: string[]; buildings?: string[]; teams?: string[] }) => void;
  readOnly?: boolean;
}

const UserScopes: React.FC<UserScopesProps> = ({ onScopesChange, readOnly = false }) => {
  const { user } = useAuth();
  const [scopes, setScopes] = useState(user?.scopes || { sites: [] as string[], buildings: [] as string[], teams: [] as string[] });
  const [newScope, setNewScope] = useState('');
  const [scopeType, setScopeType] = useState<'sites' | 'buildings' | 'teams'>('sites');

  const handleAddScope = () => {
    if (!newScope.trim()) return;

    const updatedScopes = {
      ...scopes,
      [scopeType]: [...(scopes[scopeType] || []), newScope.trim()]
    };

    setScopes(updatedScopes);
    setNewScope('');
    onScopesChange?.(updatedScopes);
  };

  const handleRemoveScope = (type: 'sites' | 'buildings' | 'teams', scope: string) => {
    const updatedScopes = {
      ...scopes,
      [type]: (scopes[type] || [] as string[]).filter((s: string) => s !== scope)
    };

    setScopes(updatedScopes);
    onScopesChange?.(updatedScopes);
  };

  const getScopeIcon = (type: string) => {
    switch (type) {
      case 'sites': return <MapPin className="h-4 w-4 text-blue-600" />;
      case 'buildings': return <Building className="h-4 w-4 text-green-600" />;
      case 'teams': return <Users className="h-4 w-4 text-purple-600" />;
      default: return <Shield className="h-4 w-4 text-gray-600" />;
    }
  };

  const getScopeLabel = (type: string) => {
    switch (type) {
      case 'sites': return 'Sites';
      case 'buildings': return 'Bâtiments';
      case 'teams': return 'Équipes';
      default: return type;
    }
  };

  const getScopeColor = (type: string) => {
    switch (type) {
      case 'sites': return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'buildings': return 'bg-green-50 border-green-200 text-green-800';
      case 'teams': return 'bg-purple-50 border-purple-200 text-purple-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  if (readOnly) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Shield className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Scopes Utilisateur</h3>
        </div>

        {/* Explication */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            🏢 Les <strong>scopes</strong> limitent votre accès aux sites, bâtiments et équipes spécifiques. 
            Vous ne voyez que les données de vos périmètres autorisés.
          </p>
        </div>

        <div className="space-y-4">
        {Object.entries(scopes).map(([type, scopeList]) => {
          const typedScopeList = scopeList as string[];
          return (
            <div key={type}>
              <div className="flex items-center space-x-2 mb-2">
                {getScopeIcon(type)}
                <span className="text-sm font-medium text-gray-700">
                  {getScopeLabel(type)} ({typedScopeList?.length || 0})
                </span>
              </div>
              
              {typedScopeList && typedScopeList.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {typedScopeList.map((scope: string, index: number) => (
                    <span
                      key={index}
                      className={`px-3 py-1 text-sm font-medium rounded-full border ${getScopeColor(type)}`}
                    >
                      {scope}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Aucun scope défini</p>
              )}
            </div>
          );
        })}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center space-x-2 mb-4">
        <Shield className="h-5 w-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">Gestion des Scopes</h3>
      </div>

      {/* Ajout de nouveau scope */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Ajouter un scope</h4>
        
        <div className="flex space-x-3">
          <select
            value={scopeType}
            onChange={(e) => setScopeType(e.target.value as 'sites' | 'buildings' | 'teams')}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="sites">Site</option>
            <option value="buildings">Bâtiment</option>
            <option value="teams">Équipe</option>
          </select>
          
          <input
            type="text"
            value={newScope}
            onChange={(e) => setNewScope(e.target.value)}
            placeholder="Nom du scope"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          
          <button
            onClick={handleAddScope}
            disabled={!newScope.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Ajouter</span>
          </button>
        </div>
      </div>

      {/* Liste des scopes */}
      <div className="space-y-4">
        {Object.entries(scopes).map(([type, scopeList]) => {
          const typedScopeList = scopeList as string[];
          return (
            <div key={type}>
              <div className="flex items-center space-x-2 mb-2">
                {getScopeIcon(type)}
                <span className="text-sm font-medium text-gray-700">
                  {getScopeLabel(type)} ({typedScopeList?.length || 0})
                </span>
              </div>
              
              {typedScopeList && typedScopeList.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {typedScopeList.map((scope: string, index: number) => (
                    <div
                      key={index}
                      className={`flex items-center space-x-2 px-3 py-1 text-sm font-medium rounded-full border ${getScopeColor(type)}`}
                    >
                      <span>{scope}</span>
                      <button
                        onClick={() => handleRemoveScope(type as 'sites' | 'buildings' | 'teams', scope)}
                        className="hover:bg-red-100 rounded-full p-1 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Aucun scope défini</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Avertissement pour les scopes vides */}
      {Object.values(scopes).every((scopeList: any) => !scopeList || scopeList.length === 0) && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              Aucun scope défini. L'utilisateur n'aura accès à aucune donnée.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserScopes;
